import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import AuthModal from './AuthModal';
import { User, LogOut, Shield, Menu, X, MessageSquare, Trophy, Newspaper, Smile, Heart, Sun, Moon, BookOpen, FileText, ChevronDown, Globe, Users, Briefcase, Calendar } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function Navbar({ theme, toggleTheme }) {
  const { user, isAdmin, isEditor, signOut } = useAuth();
  const navigate = useNavigate();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-gradient-to-r from-[#1B2A4A] to-[#2A3F6A] dark:from-slate-950 dark:to-slate-900 text-white shadow-lg shadow-[#1B2A4A]/20 dark:shadow-slate-950/20 sticky top-0 z-50 transition-all duration-300 ease-in-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex-shrink-0">
            <Link to="/" className="font-display font-bold text-xl tracking-wider flex items-center gap-2 hover:opacity-90 transition-opacity duration-300">
              <span className="text-gold drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]">UMF</span> Newspaper
            </Link>
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/news" className="hover:text-emerald-300 transition-colors duration-300 text-sm font-medium hover:-translate-y-0.5 inline-block">Global News</Link>

            {/* COMMUNITIES DROPDOWN */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 hover:text-purple-300 transition-colors duration-300 text-sm font-medium hover:-translate-y-0.5 outline-none">
                  <span>Communities</span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/section?lang=english">English Section</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/section?lang=french">French Section</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/section?lang=romanian">Romanian Section</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* TOOLS DROPDOWN */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 hover:text-blue-300 transition-colors duration-300 text-sm font-medium hover:-translate-y-0.5 outline-none">
                  <span>Tools</span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/study-hub">Study Hub</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/dental-clinic">Dental Clinic</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                  <Link to="/study-buddy">Study Buddy</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/events" className="hover:text-gold transition-colors duration-300 text-sm font-medium hover:-translate-y-0.5 inline-block">Events</Link>

            {/* CRUSHES */}
            <Link to="/crushes" className="flex items-center gap-1.5 hover:text-rose-400 transition-colors duration-300 text-sm font-medium hover:-translate-y-0.5 group">
              <Heart className="w-4 h-4 text-rose-400 group-hover:scale-110 transition-transform duration-300" />
              <span>Crushes</span>
            </Link>

            <div className="flex items-center ml-4 pl-4 border-l border-white/20">
              <button
                onClick={toggleTheme}
                className="text-white hover:text-gold transition-colors duration-300"
                aria-label="Toggle Dark Mode"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5 hover:rotate-90 transition-transform duration-300" /> : <Moon className="w-5 h-5 hover:-rotate-12 transition-transform duration-300" />}
              </button>
            </div>

            {user ? (
              <div className="flex items-center gap-3 ml-4">
                {isAdmin && (
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white hover:text-gold hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5">
                    <Shield className="w-4 h-4 mr-2" /> Admin
                  </Button>
                )}
                
                {/* 🚀 BOTTONE CHAT DESKTOP CON NOTIFICA */}
                <Button variant="ghost" size="sm" onClick={() => navigate('/messages')} className="text-white hover:text-gold hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5">
                  <div className="relative mr-2 group">
                    <MessageSquare className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                    <span className="absolute -top-2 -right-2 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white border border-[#1B2A4A] shadow-[0_0_8px_rgba(239,68,68,0.6)]">
                      3
                    </span>
                  </div>
                  Chat
                </Button>

                <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="text-white hover:text-gold hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5">
                  <User className="w-4 h-4 mr-2" /> Profile
                </Button>
                <Button variant="outline" size="sm" onClick={handleSignOut} className="border-gold text-gold hover:bg-gold hover:text-[#1B2A4A] transition-all duration-300 hover:shadow-[0_0_15px_rgba(250,204,21,0.4)] hover:-translate-y-0.5 rounded-xl">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </Button>
              </div>
            ) : (
              <Button onClick={() => setIsAuthOpen(true)} className="bg-gradient-to-r from-gold to-yellow-500 text-[#1B2A4A] hover:from-yellow-400 hover:to-yellow-500 ml-4 font-bold transition-all duration-300 hover:shadow-[0_0_15px_rgba(250,204,21,0.6)] hover:-translate-y-0.5 rounded-xl">Sign In</Button>
            )}
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={toggleTheme}
              className="text-white hover:text-gold transition-colors duration-300 md:hidden"
              aria-label="Toggle Dark Mode"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                user ? navigate('/profile') : setIsAuthOpen(true);
              }}
              className="text-white hover:text-gold transition-colors duration-300 flex items-center justify-center"
              aria-label="Profile"
            >
              <User className="w-6 h-6" />
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-gold transition-colors duration-300 md:hidden">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* MENU MOBILE */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gradient-to-b from-[#1B2A4A] to-[#2A3F6A] dark:from-slate-950 dark:to-slate-900 border-t border-white/10 px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-xl transition-all duration-300 ease-in-out">
          <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 rounded-xl text-base font-medium text-white hover:bg-white/10 transition-all duration-300">Home</Link>
          <Link to="/news" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 rounded-xl text-base font-medium text-emerald-300 hover:bg-white/10 transition-all duration-300">Global News</Link>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="communities" className="border-b-0 px-3">
              <AccordionTrigger className="py-2 hover:no-underline text-purple-300 hover:bg-white/10 rounded-xl px-0">
                <div className="flex items-center gap-2 text-base font-medium">
                  <Globe className="w-4 h-4" />
                  <span>Communities</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-2 pt-1 pl-6 flex flex-col space-y-1">
                <Link to="/section?lang=english" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-purple-300 hover:bg-white/10 transition-all duration-300">English Section</Link>
                <Link to="/section?lang=french" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-purple-300 hover:bg-white/10 transition-all duration-300">French Section</Link>
                <Link to="/section?lang=romanian" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-purple-300 hover:bg-white/10 transition-all duration-300">Romanian Section</Link>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tools" className="border-b-0 px-3">
              <AccordionTrigger className="py-2 hover:no-underline text-blue-300 hover:bg-white/10 rounded-xl px-0">
                <div className="flex items-center gap-2 text-base font-medium">
                  <Briefcase className="w-4 h-4" />
                  <span>Tools</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-2 pt-1 pl-6 flex flex-col space-y-1">
                <Link to="/study-hub" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-blue-300 hover:bg-white/10 transition-all duration-300">Study Hub</Link>
                <Link to="/dental-clinic" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-blue-300 hover:bg-white/10 transition-all duration-300">Dental Clinic</Link>
                <Link to="/study-buddy" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-blue-300 hover:bg-white/10 transition-all duration-300">Study Buddy</Link>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Link to="/events" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-base font-medium hover:text-gold hover:bg-white/10 transition-all duration-300">
            <Calendar className="w-4 h-4" />
            <span>Events</span>
          </Link>

          {/* CRUSHES MOBILE */}
          <Link to="/crushes" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-base font-medium text-rose-400 hover:bg-white/10 transition-all duration-300">
            <Heart className="w-4 h-4" />
            <span>UMF Crushes</span>
          </Link>

          {user ? (
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
              {isAdmin && (
                <Button variant="ghost" className="w-full justify-start text-white hover:text-gold hover:bg-white/10 transition-all duration-300 rounded-xl" onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}>
                  <Shield className="w-4 h-4 mr-2" /> Admin Panel
                </Button>
              )}
              
              {/* 🚀 BOTTONE CHAT MOBILE CON NOTIFICA */}
              <Button variant="ghost" className="w-full justify-between text-white hover:text-gold hover:bg-white/10 transition-all duration-300 rounded-xl" onClick={() => { navigate('/messages'); setIsMobileMenuOpen(false); }}>
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" /> Chat
                </div>
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.6)]">
                  3 New
                </span>
              </Button>

              <Button variant="ghost" className="w-full justify-start text-white hover:text-gold hover:bg-white/10 transition-all duration-300 rounded-xl" onClick={() => { navigate('/profile'); setIsMobileMenuOpen(false); }}>
                <User className="w-4 h-4 mr-2" /> My Profile
              </Button>
              <Button variant="outline" className="w-full justify-start border-gold text-gold hover:bg-gold hover:text-[#1B2A4A] transition-all duration-300 rounded-xl" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </Button>
            </div>
          ) : (
            <Button onClick={() => { setIsAuthOpen(true); setIsMobileMenuOpen(false); }} className="w-full mt-4 bg-gradient-to-r from-gold to-yellow-500 text-[#1B2A4A] hover:from-yellow-400 hover:to-yellow-500 font-bold transition-all duration-300 shadow-[0_0_10px_rgba(250,204,21,0.4)] rounded-xl">Sign In</Button>
          )}
        </div>
      )}
      <AuthModal open={isAuthOpen} onOpenChange={setIsAuthOpen} />
    </nav>
  );
}
